import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Gallery from "./Gallery";
import { Card } from "primereact/card";
import { Helmet } from "react-helmet-async";
import "../CSS/BlogPage.css";
import "primeicons/primeicons.css";
import axios from "axios";

interface BlogPost {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string;
  galleryImages: string[];
  youtubeUrl?: string;
}

const BlogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/,
      /youtube\.com\/watch\?.*v=([^&\s]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  const isArabicText = (text: string) => {
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  const getTextDirection = (text: string) => {
    return isArabicText(text) ? "rtl" : "ltr";
  };

  const copyUrl = async () => {
    try {
      const shareUrl = process.env.REACT_APP_API + "/posts/ssr/" + id;
      if (!blog) {
        await navigator.clipboard.writeText(shareUrl);
        return;
      }
      await navigator.clipboard.writeText(
        `${blog.title} Read more at: ${shareUrl}`
      );
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        axios
          .get(`${process.env.REACT_APP_API}/posts/${id}`)
          .then((response) => {
            setBlog(response.data);
          })
          .catch((error) => {
            console.error("Error fetching blog:", error);
          });
      } catch (error) {
        console.error("Error fetching blog:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  useEffect(() => {
    if (blog?.title) {
      console.log("Title should change to:", `${blog.title} - Taswik`);
      // Verify after a small delay to allow Helmet to update
      setTimeout(() => {
        console.log("Current document title:", document.title);
      }, 100);
    }
  }, [blog?.title]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading...
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Blog not found
      </div>
    );
  }

  const shareUrl = process.env.REACT_APP_API + "/posts/ssr/" + blog._id;

  return (
    <>
      <Helmet>
        <title>{blog.title} - Taswik</title>
        <meta name="description" content={blog.text.substring(0, 160)} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.text.substring(0, 160)} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        {blog.imageUrl && (
          <>
            <meta property="og:image" content={blog.imageUrl} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
          </>
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta
          name="twitter:description"
          content={blog.text.substring(0, 160)}
        />
        {blog.imageUrl && <meta name="twitter:image" content={blog.imageUrl} />}
      </Helmet>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <article className="prose lg:prose-xl mx-auto">
          <h1
            className="text-4xl font-bold mb-6 text-center"
            dir={getTextDirection(blog.title)}
          >
            {blog.title}
          </h1>

          {blog.imageUrl && (
            <img
              src={blog.imageUrl}
              alt={blog.title}
              className="blog-main-image"
            />
          )}

          <div className="social-share-buttons flex gap-4 justify-center mt-4 mb-6">
            <a
              className="social-btn whatsapp"
              rel="noreferrer noopener nofollow"
              href={`whatsapp://send?text=${encodeURIComponent(
                `${blog.title} Read more at: ${shareUrl}`
              )}`}
              title="Share on WhatsApp"
              aria-label="Share on WhatsApp"
            >
              <i className="pi pi-whatsapp"></i>
            </a>
            <a
              className="social-btn instagram"
              rel="noreferrer noopener nofollow"
              href={`https://www.instagram.com/sharer/sharer.php?u=${encodeURIComponent(
                `${blog.title} Read more at: ${shareUrl}`
              )}`}
              target="_blank"
              title="Share on Instagram"
              aria-label="Share on Instagram"
            >
              <i className="pi pi-instagram"></i>
            </a>
            <a
              className="social-btn facebook"
              rel="noreferrer noopener nofollow"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                shareUrl
              )}`}
              target="_blank"
              title="Share on Facebook"
              aria-label="Share on Facebook"
            >
              <i className="pi pi-facebook"></i>
            </a>
            <button
              type="button"
              className="social-btn share-native"
              aria-label="Share"
              onClick={() => {
                const shareData = {
                  url: shareUrl,
                };
                navigator.share(shareData).catch(console.error);
              }}
            >
              <i className="pi pi-share-alt"></i>
            </button>
            <button
              className="social-btn copy-link"
              onClick={copyUrl}
              title="Copy Link"
              aria-label="Copy Link"
            >
              <i className="pi pi-link"></i>
            </button>
          </div>

          <div className="flex justify-center">
            <Card className="w-4/5 my-8">
              <div
                className="whitespace-pre-wrap text-justify"
                dir={getTextDirection(blog.text)}
                style={{
                  textAlign: isArabicText(blog.text) ? "right" : "left",
                  lineHeight: "1.8",
                }}
              >
                {blog.text}
              </div>
            </Card>
          </div>

          {blog.galleryImages && blog.galleryImages.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold mb-4">Gallery</h2>
              <Gallery images={blog.galleryImages} isEditing={false} />
            </div>
          )}

          {blog.youtubeUrl && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold mb-4">Video</h2>
              <div className="relative w-full h-[500px]">
                {getYouTubeVideoId(blog.youtubeUrl) ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(
                      blog.youtubeUrl
                    )}`}
                    title="Blog Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <p className="text-gray-500">Invalid YouTube URL</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </article>
      </div>
    </>
  );
};

export default BlogPage;
